import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';
import { encryptedString } from '../types/encryptedString.js';

// =============================================================================
// Transform Configuration
// =============================================================================

const transformConfigSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('uppercase'),
  }),
  z.object({
    type: z.literal('lowercase'),
  }),
  z.object({
    type: z.literal('trim'),
  }),
  z.object({
    type: z.literal('regex'),
    config: z.object({
      pattern: z.string().describe('Regular expression pattern to match'),
      replacement: z.string().optional().default('').describe('Replacement string for regex matches'),
    }),
  }),
  z.object({
    type: z.literal('template'),
    config: z.object({
      template: z.string().describe('Template string supporting {value} and {fieldName} placeholders'),
    }),
  }),
  z.object({
    type: z.literal('split'),
    config: z.object({
      delimiter: z.string().describe('String delimiter to split on'),
      index: z.number().int().min(0).describe('Index of the split segment to extract'),
    }),
  }),
  z.object({
    type: z.literal('concat'),
    config: z.object({
      prefix: z.string().optional().default('').describe('Prefix string to prepend'),
      suffix: z.string().optional().default('').describe('Suffix string to append'),
    }),
  }),
  z.object({
    type: z.literal('default'),
    config: z.object({
      value: z.string().describe('Default value to use if field is empty'),
    }),
  }),
  z.object({
    type: z.literal('date_format'),
    config: z.object({
      inputFormat: z.string().optional().describe('Input date format (optional, auto-detected if not provided)'),
      outputFormat: z.string().describe('Output date format string'),
    }),
  }),
]);

export type TransformConfig = z.infer<typeof transformConfigSchema>;

// =============================================================================
// Field Mapping Configuration
// =============================================================================

const fieldMappingSchema = z.object({
  /** JSONPath expression to extract value from API response */
  source: z.string().describe('JSONPath expression to extract value from API response'),
  /** Optional chain of transforms to apply to the extracted value */
  transforms: z
    .array(transformConfigSchema)
    .optional()
    .describe('Optional chain of transforms to apply to the extracted value'),
});

export type FieldMapping = z.infer<typeof fieldMappingSchema>;

// =============================================================================
// Auth Provider Configuration
// =============================================================================

const oauth2ClientCredentialsSchema = z.object({
  type: z.literal('oauth2_client_credentials'),
  /** OAuth token endpoint URL */
  tokenEndpoint: z.string().url().describe('OAuth token endpoint URL'),
  /** Client ID - can be a secret reference like {SECRET_NAME} */
  clientId: z.string().describe('Client ID - can be a secret reference like {SECRET_NAME}'),
  /** Client secret - can be a secret reference like {SECRET_NAME} */
  clientSecret: z.string().describe('Client secret - can be a secret reference like {SECRET_NAME}'),
  /** OAuth audience (optional) */
  audience: z.string().optional().describe('OAuth audience (optional)'),
  /** OAuth scope (optional) */
  scope: z.string().optional().describe('OAuth scope (optional)'),
  /** Token cache TTL in seconds (default: 3540 = 59 minutes) */
  cacheTtlSeconds: z
    .number()
    .int()
    .positive()
    .optional()
    .default(3540)
    .describe('Token cache TTL in seconds (default: 3540 = 59 minutes)'),
});

const apiKeyAuthSchema = z.object({
  type: z.literal('api_key'),
  /** Header name for the API key */
  headerName: z.string().describe('Header name for the API key'),
  /** API key value - can be a secret reference like {SECRET_NAME} */
  apiKey: z.string().describe('API key value - can be a secret reference like {SECRET_NAME}'),
});

const basicAuthSchema = z.object({
  type: z.literal('basic'),
  /** Username - can be a secret reference */
  username: z.string().describe('Username - can be a secret reference'),
  /** Password - can be a secret reference */
  password: z.string().describe('Password - can be a secret reference'),
});

const authProviderSchema = z.discriminatedUnion('type', [
  oauth2ClientCredentialsSchema,
  apiKeyAuthSchema,
  basicAuthSchema,
]);

export type AuthProviderConfig = z.infer<typeof authProviderSchema>;

// Export individual auth provider types for direct use
export type OAuth2Config = z.infer<typeof oauth2ClientCredentialsSchema>;
export type ApiKeyAuthConfig = z.infer<typeof apiKeyAuthSchema>;
export type BasicAuthConfig = z.infer<typeof basicAuthSchema>;

// =============================================================================
// Custom Header Configuration
// =============================================================================

/**
 * Custom headers schema - a record of header name to header value.
 * Both keys and values support template variable interpolation (e.g., {cdi.api_version}).
 * Example: { "api-version": "4", "x-custom-header": "{cdi.custom_value}" }
 */
const customHeadersSchema = z
  .record(z.string(), z.string())
  .describe('Custom headers as key-value pairs. Both header names and values support template variable interpolation.');

export type CustomHeaders = z.infer<typeof customHeadersSchema>;

// =============================================================================
// Autocomplete Trigger Configuration
// =============================================================================

const autocompleteTriggerSchema = z.object({
  /** Whether this trigger is enabled */
  enabled: z.boolean().describe('Whether this trigger is enabled'),
  /** Which auth provider to use (references key in authProviders) */
  authProvider: z.string().describe('Which auth provider to use (references key in authProviders)'),
  /** HTTP method for the API call */
  httpMethod: z.enum(['GET', 'POST']).describe('HTTP method for the API call'),
  /** API endpoint URL - supports {search_query} interpolation */
  endpoint: z.string().describe('API endpoint URL - supports {search_query} interpolation'),
  /** Request body template for POST requests (optional) */
  requestBody: z.string().optional().describe('Request body template for POST requests (optional)'),
  /** Optional custom headers to include with the request - supports template variable interpolation */
  headers: customHeadersSchema
    .optional()
    .default({})
    .describe(
      'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })'
    ),
  /** Minimum characters before triggering autocomplete */
  minChars: z.number().int().min(1).optional().default(3).describe('Minimum characters before triggering autocomplete'),
  /** Debounce delay in milliseconds */
  debounceMs: z.number().int().min(0).optional().default(300).describe('Debounce delay in milliseconds'),
  /** Maximum results to return */
  maxResults: z.number().int().min(1).max(50).optional().default(10).describe('Maximum results to return'),
  /** Field mappings for autocomplete output */
  mappings: z
    .object({
      /** Unique identifier for the result (required) */
      id: fieldMappingSchema,
      /** Primary display text shown in dropdown (required) */
      displayValue: fieldMappingSchema,
      /** Secondary text shown below display value (optional) */
      subtext: fieldMappingSchema.optional(),
    })
    .describe('Field mappings for autocomplete output'),
});

export type AutocompleteConfig = z.infer<typeof autocompleteTriggerSchema>;

// =============================================================================
// Canonical Types
// =============================================================================
// These define the standard request/response formats for the proxy layer.
// The form code only needs to know these 4 canonical types:
// - AutocompleteRequest / AutocompleteResult (for typeahead dropdowns)
// - FetchRequest / FetchResult (for data fetching)
//
// The proxy handles mapping vendor-specific APIs to/from these canonical formats.

/**
 * Canonical request for autocomplete triggers.
 * Form code sends this to request typeahead suggestions.
 */
export type AutocompleteRequest = {
  /** The trigger type (e.g., 'consumer-name', 'reference-number', 'consumer-vin') */
  triggerType: string;
  /** The user's search input */
  searchQuery: string;
};

/**
 * Canonical fields for autocomplete results.
 * All autocomplete triggers return this structure.
 */
export const AUTOCOMPLETE_FIELDS = {
  /** Unique identifier for the result */
  ID: 'id',
  /** Primary display text shown in dropdown */
  DISPLAY_VALUE: 'displayValue',
  /** Secondary text shown below display value */
  SUBTEXT: 'subtext',
} as const;

/**
 * Canonical response for autocomplete triggers.
 * Each item in the dropdown list conforms to this structure.
 */
export type AutocompleteResult = {
  id: string | number;
  displayValue: string;
  subtext?: string;
};

/**
 * Canonical request for fetch triggers.
 * Form code sends this to request detailed data.
 */
export type FetchRequest = {
  /** The trigger type (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail') */
  triggerType: string;
  /** The value to look up (e.g., contract ID, customer ID, VIN) */
  lookupValue: string;
};

/**
 * Canonical fields for fetch results.
 * This single canonical type covers all fetch triggers.
 * Not all fields will be populated - it depends on what data
 * the 3rd party API can provide and what the config maps.
 *
 * NOTE: These use generic terminology, not vendor-specific terms.
 * E.g., "referenceNumber" not "contractNumber" (Dealerware-specific).
 */
export const FETCH_RESULT_FIELDS = {
  // Identity/Display
  ID: 'id',
  DISPLAY_LABEL: 'displayLabel',

  // Reference
  REFERENCE_NUMBER: 'referenceNumber',

  // Customer Information
  CUSTOMER_NAME: 'customerName',
  CUSTOMER_PHONE: 'customerPhone',
  CUSTOMER_EMAIL: 'customerEmail',
  CUSTOMER_ADDRESS_LINE1: 'customerAddressLine1',
  CUSTOMER_ADDRESS_LINE2: 'customerAddressLine2',
  CUSTOMER_ADDRESS_CITY: 'customerAddressCity',
  CUSTOMER_ADDRESS_STATE: 'customerAddressState',
  CUSTOMER_ADDRESS_ZIP: 'customerAddressZip',

  // Vehicle Information
  VIN: 'vin',
  YEAR: 'year',
  MAKE: 'make',
  MODEL: 'model',
  COLOR: 'color',
  STOCK_NUMBER: 'stockNumber',
  LICENSE_PLATE: 'licensePlate',
} as const;

/**
 * Canonical response for fetch triggers.
 * A single unified type for all fetch responses.
 *
 * The form code that fires a fetch trigger is responsible for:
 * 1. Understanding its own context (e.g., "this was triggered from loaner VIN selection")
 * 2. Interpreting which fields from this canonical response are relevant
 * 3. Mapping those fields to the appropriate form fields or UI actions
 *
 * Not all fields will be populated - the proxy maps whatever the 3rd party API
 * provides according to the connector configuration.
 */
export type FetchResult = {
  // Identity/Display - useful when presenting results to user
  id?: string | number;
  displayLabel?: string;

  // Reference
  referenceNumber?: string;

  // Customer Information
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerAddressLine1?: string;
  customerAddressLine2?: string;
  customerAddressCity?: string;
  customerAddressState?: string;
  customerAddressZip?: string;

  // Vehicle Information
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  color?: string;
  stockNumber?: string;
  licensePlate?: string;
};

// =============================================================================
// Fetch Trigger Configuration
// =============================================================================

/**
 * Unified mapping schema for fetch triggers.
 * Maps 3rd party API response fields to canonical FetchResult fields.
 * Only canonical field names are allowed - this ensures type safety
 * and prevents arbitrary field mappings from leaking vendor-specific data.
 */
const fetchResultMappingsSchema = z
  .object({
    // Identity/Display
    id: fieldMappingSchema.optional(),
    displayLabel: fieldMappingSchema.optional(),

    // Reference
    referenceNumber: fieldMappingSchema.optional(),

    // Customer Information
    customerName: fieldMappingSchema.optional(),
    customerPhone: fieldMappingSchema.optional(),
    customerEmail: fieldMappingSchema.optional(),
    customerAddressLine1: fieldMappingSchema.optional(),
    customerAddressLine2: fieldMappingSchema.optional(),
    customerAddressCity: fieldMappingSchema.optional(),
    customerAddressState: fieldMappingSchema.optional(),
    customerAddressZip: fieldMappingSchema.optional(),

    // Vehicle Information
    vin: fieldMappingSchema.optional(),
    year: fieldMappingSchema.optional(),
    make: fieldMappingSchema.optional(),
    model: fieldMappingSchema.optional(),
    color: fieldMappingSchema.optional(),
    stockNumber: fieldMappingSchema.optional(),
    licensePlate: fieldMappingSchema.optional(),
  })
  .strict();

const enrichmentSchema = z.object({
  condition: z
    .string()
    .optional()
    .describe(
      `JSONPath condition evaluated against the **raw primary API response**.

Enrichment is skipped if the condition returns a falsy value (\`null\`, \`undefined\`, \`""\`, \`0\`, \`false\`, or empty array).

**Condition Styles:**

| Style | Syntax | Behavior |
|-------|--------|----------|
| Simple existence | \`$.id\` | Truthy if field exists and has value |
| Filter expression | \`$[?(@.field == "value")]\` | Truthy if filter matches |

**Filter Expression Examples:**

| Condition | Description |
|-----------|-------------|
| \`$[?(@.tier == "premium")]\` | Tier equals "premium" |
| \`$.vehicle[?(@.year >= 2020)]\` | Vehicle year is 2020 or newer |
| \`$.features[?(@ == "roadside_assistance")]\` | Features array contains value |
| \`$.customer[?(@.membership_level == "gold")]\` | Nested property equals value |
| \`$.vehicles[?(@.primary == true)]\` | Array contains object with primary=true |`
    ),
  authProvider: z
    .string()
    .optional()
    .describe(
      `Auth provider to use for this enrichment request.

Defaults to the parent fetch trigger's \`authProvider\` if not specified. Use this to authenticate enrichment calls against a different API than the primary request.`
    ),
  httpMethod: z
    .enum(['GET', 'POST', 'PUT', 'PATCH'])
    .default('GET')
    .describe(
      `HTTP method for the enrichment API call.

Defaults to \`GET\`. Use \`POST\`, \`PUT\`, or \`PATCH\` when the enrichment endpoint requires a request body.`
    ),
  endpoint: z.string().describe(
    `Endpoint URL with variable interpolation.

**Supported Variables:**

| Variable | Source | Example |
|----------|--------|---------|
| \`{primary.fieldName}\` | Mapped field from primary response | \`{primary.customerId}\` |
| \`{primary_raw.$.path}\` | JSONPath from raw primary response | \`{primary_raw.$.data[0].id}\` |
| \`{lookup_value}\` | Original lookup value | \`{lookup_value}\` |

**Example:**
\`\`\`
https://api.example.com/customers/{primary.customerId}/address
\`\`\`

All interpolated values are URL-encoded automatically.`
  ),
  requestBody: z
    .string()
    .optional()
    .describe(
      `Request body template for \`POST\`, \`PUT\`, or \`PATCH\` requests.

Supports the same variable interpolation as \`endpoint\`:
- \`{primary.fieldName}\` - Mapped field from primary response
- \`{primary_raw.$.path}\` - JSONPath from raw primary response
- \`{lookup_value}\` - Original lookup value

**Example:**
\`\`\`json
{"customerId": "{primary.customerId}", "action": "getAddress"}
\`\`\``
    ),
  headers: customHeadersSchema
    .optional()
    .default({})
    .describe(
      `Optional custom headers to include with the enrichment request.

Supports the same variable interpolation as \`endpoint\`:
- \`{primary.fieldName}\` - Mapped field from primary response
- \`{primary_raw.$.path}\` - JSONPath from raw primary response
- \`{lookup_value}\` - Original lookup value

**Example:**
\`\`\`json
{ "api-version": "4", "x-tenant-id": "{primary.tenantId}" }
\`\`\``
    ),
  mappings: fetchResultMappingsSchema.describe(
    `Field mappings applied to the enrichment response.

Mapped values are **merged** with the primary response result. Non-null enrichment values will overwrite existing fields from the primary response.

Uses the same JSONPath syntax as primary fetch trigger mappings.`
  ),
});

export type EnrichmentConfig = z.infer<typeof enrichmentSchema>;

/**
 * Fetch trigger configuration.
 * Defines how to call a 3rd party API and map the response to the canonical FetchResult.
 */
const fetchTriggerSchema = z.object({
  /** Whether this trigger is enabled */
  enabled: z.boolean().describe('Whether this trigger is enabled'),
  /** Which auth provider to use (references key in authProviders) */
  authProvider: z.string().describe('Which auth provider to use (references key in authProviders)'),
  /** HTTP method for the API call */
  httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH']).describe('HTTP method for the API call'),
  /** API endpoint URL - supports {lookup_value} interpolation */
  endpoint: z.string().describe('API endpoint URL - supports {lookup_value} interpolation'),
  /** Request body template for POST/PUT/PATCH requests (optional) */
  requestBody: z.string().optional().describe('Request body template for POST/PUT/PATCH requests (optional)'),
  /** Optional custom headers to include with the request - supports template variable interpolation */
  headers: customHeadersSchema
    .optional()
    .default({})
    .describe(
      'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })'
    ),
  /**
   * Whether fetch results should overwrite existing form field values.
   * When true, all mapped fields from the fetch response will replace existing values.
   * When false (default), only empty form fields will be populated.
   */
  overwriteExisting: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      'Whether fetch results should overwrite existing form field values. When true, all mapped fields from the fetch response will replace existing values. When false (default), only empty form fields will be populated.'
    ),
  /** Field mappings from 3rd party API response to canonical FetchResult */
  mappings: fetchResultMappingsSchema.describe('Field mappings from 3rd party API response to canonical FetchResult'),
  enrichments: z
    .array(enrichmentSchema)
    .optional()
    .default([])
    .describe(
      `Optional enrichments for chained API calls.

Each enrichment makes a **secondary API call** using data from the primary response, then merges the results into the final \`FetchResult\`.

**Use Cases:**
- Fetch customer address from a separate endpoint
- Get additional details that require the primary record's ID
- Call different APIs conditionally based on primary response data

**Execution Flow:**
1. Primary fetch request executes and maps response
2. Each enrichment's \`condition\` is evaluated against raw primary response
3. Enrichments with passing conditions execute in order
4. Results merge into final response (non-null values overwrite)

**Error Handling:**
Enrichment failures are non-fatal. If an enrichment fails, the primary data is preserved and processing continues with remaining enrichments.`
    ),
});

export type FetchConfig = z.infer<typeof fetchTriggerSchema>;

// =============================================================================
// Main Connector Triggers Configuration
// =============================================================================

export const schema = z.object({
  /** Master enable/disable toggle for the entire integration */
  enabled: z.boolean().describe('Master enable/disable toggle for the entire integration'),

  /**
   * Encrypted secrets storage.
   * Keys are secret names (e.g., "DEALERWARE_CLIENT_ID")
   * Values are encrypted strings (e.g., "encrypted:v1:abc123...")
   */
  secrets: z
    .record(z.string(), encryptedString)
    .optional()
    .default({})
    .describe(
      'Encrypted secrets storage. Keys are secret names (e.g., "DEALERWARE_CLIENT_ID"). Values are encrypted strings (e.g., "encrypted:v1:abc123...")'
    ),

  /**
   * Auth provider configurations.
   * Keys are provider names referenced by triggers (e.g., "dealerware-oauth")
   */
  authProviders: z
    .record(z.string(), authProviderSchema)
    .optional()
    .default({})
    .describe(
      'Auth provider configurations. Keys are provider names referenced by triggers (e.g., "dealerware-oauth")'
    ),

  /**
   * Autocomplete trigger configurations.
   * Keys are trigger types matching form field triggers:
   * - consumer-name
   * - consumer-phone
   * - reference-number
   * - consumer-vin
   * - loaner-vin
   * - stock-number
   */
  autocompleteTriggers: z
    .record(z.string(), autocompleteTriggerSchema)
    .optional()
    .default({})
    .describe(
      'Autocomplete trigger configurations. Keys are trigger types matching form field triggers: consumer-name, consumer-phone, reference-number, consumer-vin, loaner-vin, stock-number'
    ),

  /**
   * Fetch trigger configurations.
   * Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail').
   * The trigger type is arbitrary - form code defines what trigger types it fires,
   * and the config defines how to handle each one.
   */
  fetchTriggers: z
    .record(z.string(), fetchTriggerSchema)
    .optional()
    .default({})
    .describe(
      "Fetch trigger configurations. Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail'). The trigger type is arbitrary - form code defines what trigger types it fires, and the config defines how to handle each one."
    ),
});

export type IntegrationConfig = z.infer<typeof schema>;

// =============================================================================
// Export Schemas for use in sdk-connectors
// =============================================================================

export const TransformConfigSchema = transformConfigSchema;
export const FieldMappingSchema = fieldMappingSchema;
export const CustomHeadersSchema = customHeadersSchema;
export const AuthProviderSchema = authProviderSchema;
export const AutocompleteTriggerSchema = autocompleteTriggerSchema;
export const FetchTriggerSchema = fetchTriggerSchema;
export const FetchResultMappingsSchema = fetchResultMappingsSchema;
export const EnrichmentSchema = enrichmentSchema;
export const IntegrationSchema = schema;

// =============================================================================
// Definition
// =============================================================================

export const definition = define({
  key: 'connector_triggers',
  schema,
  kind: DEFINITION_KINDS.SETTING,
  writableAtLevels: [LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN, ROLES.DEALER_ADMIN, ROLES.DEALER_SUPER_ADMIN],
  label: 'Connector Triggers',
  description: 'Configure triggered connectors for move form autocomplete and data fetching from partner APIs',
  category: CATEGORIES.INTEGRATIONS.key,
});
